import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import api from "src/api/rest";
import {
    getMarketplaceBootstrap,
    MarketplaceApiError,
    provisionMarketplaceBrandLocation,
} from "src/api/marketplace/client";
import {
    MarketplaceBootstrap,
    MarketplaceLocation,
} from "src/api/marketplace/types";
import { Alert, AlertDescription, AlertTitle } from "src/@/components/ui/alert";
import { Button } from "src/@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "src/@/components/ui/card";
import { firebaseAuth } from "#app/firebase";
import { useAppDispatch } from "src/redux/hooks";
import { setUser } from "src/redux/slices";

type LoadState =
    | { status: "loading" }
    | { status: "ready"; bootstrap: MarketplaceBootstrap }
    | { status: "error"; code: string; message: string };

type PreferredSelection = {
    brandId?: string;
    locationId?: string;
};

const isActionableLocation = (location: MarketplaceLocation) =>
    location.storeId === null;

const getActionableLocationId = (
    locations: MarketplaceLocation[],
    preferredLocationId?: string,
) =>
    locations.find(
        (location) =>
            location.id === preferredLocationId &&
            isActionableLocation(location),
    )?.id ??
    locations.find(isActionableLocation)?.id ??
    "";

const getLocationOptionLabel = (location: MarketplaceLocation) =>
    [
        location.name === "Location" ? null : location.name,
        location.address,
        isActionableLocation(location) ? "Needs setup" : "Already set up",
    ]
        .filter(Boolean)
        .join(" — ");

const asMarketplaceError = (error: unknown) =>
    error instanceof MarketplaceApiError
        ? { code: error.code, message: error.message }
        : {
              code: "marketplace_onboarding_failed",
              message: "Marketplace onboarding failed. Retry shortly.",
          };

export const MarketplaceOnboardingPage = () => {
    const dispatch = useAppDispatch();
    const isMountedRef = useRef(true);
    const loadGenerationRef = useRef(0);
    const provisionGenerationRef = useRef(0);
    const provisionInFlightRef = useRef(false);
    const [loadState, setLoadState] = useState<LoadState>({
        status: "loading",
    });
    const [selectedBrandId, setSelectedBrandId] = useState("");
    const [selectedLocationId, setSelectedLocationId] = useState("");
    const [isProvisioning, setIsProvisioning] = useState(false);
    const [provisionError, setProvisionError] = useState<{
        code: string;
        message: string;
    } | null>(null);

    const loadBootstrap = useCallback(
        async ({ brandId, locationId }: PreferredSelection = {}) => {
            const generation = ++loadGenerationRef.current;
            const firebaseUser = firebaseAuth.currentUser;
            const isCurrentLoad = () =>
                isMountedRef.current &&
                generation === loadGenerationRef.current &&
                firebaseAuth.currentUser === firebaseUser;

            setLoadState({ status: "loading" });
            setProvisionError(null);
            try {
                if (!firebaseUser) {
                    throw new MarketplaceApiError(
                        "firebase_session_missing",
                        "Firebase session is no longer available",
                    );
                }
                const bootstrap = await getMarketplaceBootstrap();
                if (!isCurrentLoad()) return;

                const initialBrand =
                    bootstrap.brands.find((brand) => brand.id === brandId) ??
                    bootstrap.brands.find((brand) => brand.isPrimary) ??
                    bootstrap.brands[0];
                if (!initialBrand) {
                    throw new MarketplaceApiError(
                        "marketplace_no_active_brand_membership",
                        "No active Marketplace brand membership exists for this user",
                    );
                }

                setSelectedBrandId(initialBrand.id);
                setSelectedLocationId(
                    getActionableLocationId(initialBrand.locations, locationId),
                );
                setLoadState({ status: "ready", bootstrap });
            } catch (error) {
                if (!isCurrentLoad()) return;
                setLoadState({
                    status: "error",
                    ...asMarketplaceError(error),
                });
            }
        },
        [],
    );

    useEffect(() => {
        isMountedRef.current = true;
        void loadBootstrap();
        return () => {
            isMountedRef.current = false;
            loadGenerationRef.current += 1;
            provisionGenerationRef.current += 1;
        };
    }, [loadBootstrap]);

    const selectedBrand = useMemo(
        () =>
            loadState.status === "ready"
                ? loadState.bootstrap.brands.find(
                      (brand) => brand.id === selectedBrandId,
                  )
                : undefined,
        [loadState, selectedBrandId],
    );

    const selectedLocation = useMemo(
        () =>
            selectedBrand?.locations.find(
                (location) => location.id === selectedLocationId,
            ),
        [selectedBrand, selectedLocationId],
    );

    const selectBrand = (brandId: string) => {
        if (loadState.status !== "ready") return;
        const brand = loadState.bootstrap.brands.find(
            (candidate) => candidate.id === brandId,
        );
        setSelectedBrandId(brandId);
        setSelectedLocationId(
            brand ? getActionableLocationId(brand.locations) : "",
        );
        setProvisionError(null);
    };

    const provisionLocation = async () => {
        if (
            !selectedLocation ||
            !isActionableLocation(selectedLocation) ||
            provisionInFlightRef.current
        )
            return;

        const generation = ++provisionGenerationRef.current;
        const brandId = selectedBrandId;
        const locationId = selectedLocation.id;
        const firebaseUser = firebaseAuth.currentUser;
        const isCurrentProvision = () =>
            isMountedRef.current &&
            generation === provisionGenerationRef.current &&
            firebaseAuth.currentUser === firebaseUser;

        provisionInFlightRef.current = true;
        setIsProvisioning(true);
        setProvisionError(null);
        try {
            if (!firebaseUser) {
                throw new MarketplaceApiError(
                    "firebase_session_missing",
                    "Firebase session is no longer available",
                );
            }
            const result = await provisionMarketplaceBrandLocation(locationId);
            if (!isCurrentProvision()) return;
            if (result.ownershipConfirmed !== true) {
                throw new MarketplaceApiError(
                    "marketplace_ownership_not_confirmed",
                    "Store setup completed without confirmed ownership. Retry before entering Marketplace.",
                );
            }

            const token = await firebaseUser.getIdToken();
            if (!isCurrentProvision()) return;
            const response = await api.users.getMe(token);
            if (!isCurrentProvision()) return;

            const { user, hash } = response.data;
            if (!user.snackpassPermissions.storeIds.includes(result.storeId)) {
                throw new MarketplaceApiError(
                    "store_association_missing_after_ensure",
                    "Store setup completed, but your Store association is not available yet. Retry before entering Marketplace.",
                );
            }

            dispatch(setUser({ ...user, intercomHash: hash }));
            await loadBootstrap({
                brandId,
                locationId,
            });
        } catch (error) {
            if (isCurrentProvision()) {
                setProvisionError(asMarketplaceError(error));
            }
        } finally {
            if (generation === provisionGenerationRef.current) {
                provisionInFlightRef.current = false;
            }
            if (isCurrentProvision()) {
                setIsProvisioning(false);
            }
        }
    };

    if (loadState.status === "loading") {
        return (
            <div className="flex flex-1 items-center justify-center p-12">
                <Loader2
                    className="h-8 w-8 animate-spin text-muted-foreground"
                    aria-label="Loading Marketplace onboarding"
                />
            </div>
        );
    }

    if (loadState.status === "error") {
        return (
            <div className="mx-auto flex w-full max-w-2xl flex-1 items-center px-6 py-12">
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>
                        Marketplace onboarding is incomplete
                    </AlertTitle>
                    <AlertDescription className="mt-2 space-y-4">
                        <p>{loadState.message}</p>
                        <p className="font-mono text-xs">{loadState.code}</p>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => void loadBootstrap()}
                        >
                            Retry
                        </Button>
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    const noUsableLocation = !selectedBrand?.locations.length;
    const allLocationsMapped = Boolean(
        selectedBrand?.locations.length &&
            !selectedBrand.locations.some(isActionableLocation),
    );
    const isSelectedLocationMapped = Boolean(
        selectedLocation && !isActionableLocation(selectedLocation),
    );

    return (
        <div className="mx-auto w-full max-w-3xl px-6 py-12">
            <Card>
                <CardHeader>
                    <CardTitle>Set up your Marketplace Store</CardTitle>
                    <CardDescription>
                        Choose from the active brands and locations available to
                        your Snackpass account.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    <label className="block space-y-2 text-sm font-medium">
                        Brand
                        <select
                            aria-label="Brand"
                            className="mt-2 h-10 w-full rounded-md border bg-background px-3"
                            disabled={isProvisioning}
                            value={selectedBrandId}
                            onChange={(event) =>
                                selectBrand(event.target.value)
                            }
                        >
                            {loadState.bootstrap.brands.map((brand) => (
                                <option key={brand.id} value={brand.id}>
                                    {brand.name}
                                </option>
                            ))}
                        </select>
                    </label>

                    {noUsableLocation ? (
                        <Alert variant="warning">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>No usable location</AlertTitle>
                            <AlertDescription>
                                This brand has no active locations. Add an
                                active location before Store setup can begin.
                                <span className="mt-2 block font-mono text-xs">
                                    marketplace_no_active_location
                                </span>
                            </AlertDescription>
                        </Alert>
                    ) : (
                        <label className="block space-y-2 text-sm font-medium">
                            Location
                            <select
                                aria-label="Location"
                                className="mt-2 h-10 w-full rounded-md border bg-background px-3"
                                disabled={isProvisioning || allLocationsMapped}
                                value={selectedLocationId}
                                onChange={(event) => {
                                    setSelectedLocationId(event.target.value);
                                    setProvisionError(null);
                                }}
                            >
                                <option value="" disabled>
                                    {allLocationsMapped
                                        ? "No locations need setup"
                                        : "Select a location"}
                                </option>
                                {selectedBrand?.locations.map((location) => (
                                    <option
                                        key={location.id}
                                        value={location.id}
                                        disabled={
                                            !isActionableLocation(location)
                                        }
                                    >
                                        {getLocationOptionLabel(location)}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}

                    {selectedLocation ? (
                        <div className="rounded-md border bg-muted/30 p-4 text-sm">
                            <p className="font-medium">Marketplace mapping</p>
                            <dl className="mt-3 grid gap-3 sm:grid-cols-2">
                                <div>
                                    <dt className="text-muted-foreground">
                                        Chain
                                    </dt>
                                    <dd className="mt-1 break-all font-mono text-xs">
                                        {selectedBrand?.chainId ?? "Not mapped"}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-muted-foreground">
                                        Store
                                    </dt>
                                    <dd className="mt-1 break-all font-mono text-xs">
                                        {selectedLocation.storeId ??
                                            "Not mapped"}
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    ) : null}

                    {allLocationsMapped ? (
                        <Alert>
                            <CheckCircle2 className="h-4 w-4" />
                            <AlertTitle>
                                All locations are already set up
                            </AlertTitle>
                            <AlertDescription>
                                Choose another brand with a location that needs
                                setup to continue onboarding.
                            </AlertDescription>
                        </Alert>
                    ) : null}

                    {provisionError ? (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>Store setup did not finish</AlertTitle>
                            <AlertDescription>
                                {provisionError.message}
                                <span className="mt-2 block font-mono text-xs">
                                    {provisionError.code}
                                </span>
                            </AlertDescription>
                        </Alert>
                    ) : null}

                    <Button
                        type="button"
                        disabled={
                            noUsableLocation ||
                            !selectedLocation ||
                            isSelectedLocationMapped ||
                            isProvisioning
                        }
                        onClick={() => void provisionLocation()}
                    >
                        {isSelectedLocationMapped ? (
                            "Store already set up"
                        ) : isProvisioning ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Starting Store setup…
                            </>
                        ) : (
                            "Start Store setup"
                        )}
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
};
